import { ChangeEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as yup from "yup";
import { cpf, cnpj } from "cpf-cnpj-validator";
import axios from "../../../../services/axios";
import Constants from "../../../../constants/Index";
import { fetchStateCities, fetchStatesUF } from "../../../../services/ibge";
import { useOnboarding } from "../../../../hooks/useOnboarding";
import { ButtonV2, formatErrorMessageV2, OptionV2, RemoveButtonV2 } from "../../../../components/FormControlsV2";
import { OnboardingFooterV2 } from "../../../../components/OnboardingV2/OnboardingFooterV2";
import { FarmFormV2, FarmFormValueV2 } from "../../../../components/FormsV2";
import { fromFarmFormValue, toFarmFormValue, toUserOptions } from "../adapters/propertyForms";
import { persistPayload } from "../adapters/payload";
import { generateuuid } from "../../../../utils/uuid/generate";
import { hasErrors, validateV2, ValidationErrorsV2 } from "../adapters/validation";
import { DrawOnMapSlotV2 } from "../../../../components/OnboardingV2/DrawOnMapSlotV2";
import { SliderComponent } from "../../../../components/EditOnboarding/Components/Slider/Index";
import { readFileKML } from "../../../../utils/kml/readFile";
import { ICoords } from "../../../../@types/GoogleMaps/ICoord";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

function createFarmSchema(initialsUsed: string[]) {
  return yup.object().shape({
    name: yup.string().matches(/^[^\s]+(\s+[^\s]+)*$/, () => i18n.t("internalValidation.invalidNameValue")).required(() => i18n.t("internalValidation.farmRequired")),
    initials: yup
      .string()
      .matches(/^[^\s]+(\s+[^\s]+)*$/, () => i18n.t("internalValidation.invalidInitials"))
      .max(3, () => i18n.t("internalValidation.initialsLength"))
      .required(() => i18n.t("internalValidation.initialsRequired"))
      .notOneOf(initialsUsed, () => i18n.t("internalValidation.initialsInUse")),
    document: yup
      .string()
      .notRequired()
      .test("documentCPF", () => i18n.t("internalValidation.invalidCpf"), (value) => !value || value.length > 11 || cpf.isValid(value))
      .test("documentCNPJ", () => i18n.t("internalValidation.invalidCnpj"), (value) => !value || value.length <= 11 || cnpj.isValid(value)),
    manager_user_id: yup.object().nullable().required(() => i18n.t("internalValidation.responsibleRequired")),
    address: yup.object({
      state: yup.object().nullable().required(() => i18n.t("internalValidation.stateRequired")),
      city: yup.object().nullable().required(() => i18n.t("internalValidation.cityRequired")),
    }),
    map_coords: yup.array().min(1, () => i18n.t("internalValidation.farmMapRequired")),
  });
}

function createFarmBase(): FarmFormValueV2 {
  return {
    uuid: generateuuid(),
    name: "",
    initials: "",
    document: "",
    state_registration: "",
    manager_user_id: null,
    address: { state: null, city: null },
    season: { rain_initial: 3, dry_initial: 8 },
    map_coords: [],
  };
}

function farmViewKey(farm: FarmFormValueV2, index: number) {
  return farm.uuid || String(farm.id ?? index);
}

function getChangedFarmFields(previous: FarmFormValueV2, value: FarmFormValueV2) {
  const changedFields = Object.keys(value).filter(
    (field) => field !== "address" && field !== "season" &&
      previous[field as keyof FarmFormValueV2] !== value[field as keyof FarmFormValueV2]
  );
  if (previous.address.state !== value.address.state) changedFields.push("address.state");
  if (previous.address.city !== value.address.city) changedFields.push("address.city");
  if (previous.season.rain_initial !== value.season.rain_initial) changedFields.push("season.rain_initial");
  if (previous.season.dry_initial !== value.season.dry_initial) changedFields.push("season.dry_initial");
  return changedFields;
}

function equalErrors(previous: Record<string, ValidationErrorsV2>, next: Record<string, ValidationErrorsV2>) {
  return Object.keys(previous).length === Object.keys(next).length && Object.keys(next).every((key) =>
    previous[key] && Object.keys(previous[key]).length === Object.keys(next[key]).length &&
    Object.entries(next[key]).every(([field, message]) => previous[key][field] === message)
  );
}

function getFarmMapCoords(coords?: any): ICoords {
  if (Array.isArray(coords)) return coords as ICoords;
  if (Array.isArray(coords?.coords)) return coords.coords;

  if (typeof coords === "string") {
    try {
      return getFarmMapCoords(JSON.parse(coords));
    } catch {
      return [];
    }
  }

  return [];
}

function hasCoords(coords?: any) {
  return getFarmMapCoords(coords).length > 0;
}

export function CreateFarmsV2({ importFlow = false }: { importFlow?: boolean }) {
  const { t } = useTranslation();
  const {
    payload,
    persistDraft = persistPayload,
    setPayload,
    setBlockNextStep,
    setShowScreen,
    updateStateManually,
    userProgress,
    apiFieldErrors,
    setApiFieldErrors,
    clearStepError,
    screenNumber = 1,
    subScreenNumber = 3,
  } = useOnboarding();
  const users = payload?.users;
  const managers = useMemo(() => toUserOptions({ users }, Constants.PROFILE_CODE_GERENTE), [users]);
  // Read the draft on entry. Loading dropdown options must not rehydrate edited forms.
  const [farms, setFarms] = useState<FarmFormValueV2[]>(() => payload?.farms?.length
    ? payload.farms.map((farm: any) => toFarmFormValue(farm, managers))
    : [createFarmBase()]);
  const [states, setStates] = useState<OptionV2[]>([]);
  const [citiesByFarm, setCitiesByFarm] = useState<Record<string, OptionV2[]>>({});
  const [errors, setErrors] = useState<Record<string, ValidationErrorsV2>>({});
  const [kmlErrors, setKmlErrors] = useState<Record<string, string>>({});
  const [kmlFileNames, setKmlFileNames] = useState<Record<string, string>>({});
  const [initialsUsed, setInitialsUsed] = useState<string[]>([]);
  const [showFullValidation, setShowFullValidation] = useState(false);
  const userProgressRef = useRef(userProgress);
  const validationSequence = useRef(0);
  const mounted = useRef(false);
  const hasApiErrors = useMemo(() => {
    const farmsCount = Math.max(farms.length, payload?.farms?.length || 0);
    return Object.keys(apiFieldErrors).some((field) => {
      const match = field.match(/^farms\[(\d+)\]\./);
      return !!match && Number(match[1]) < farmsCount;
    });
  }, [apiFieldErrors, farms.length, payload?.farms?.length]);
  const latestValidation = useRef({ farms, initialsUsed, apiFieldErrors, t });

  useLayoutEffect(() => {
    latestValidation.current = { farms, initialsUsed, apiFieldErrors, t };
  }, [farms, initialsUsed, apiFieldErrors, t]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const validateFarms = useCallback(async (farmsToValidate = farms, fullValidation = showFullValidation) => {
    const sequence = ++validationSequence.current;
    const farmSchema = createFarmSchema(initialsUsed);
    const validationEntries = await Promise.all(
      farmsToValidate.map(async (farm, index) => {
        const validation = fullValidation ? await validateV2(farmSchema, farm) : {};
        const sameInitials = farmsToValidate.filter((item) => item.initials && item.initials === farm.initials).length;
        if (sameInitials > 1 || (farm.initials && initialsUsed.includes(farm.initials))) {
          validation.initials = t("internalValidation.initialsInUse");
        }
        return [String(index), validation] as const;
      })
    );
    const latest = latestValidation.current;
    if (!mounted.current || latest.farms !== farmsToValidate || latest.initialsUsed !== initialsUsed ||
      latest.apiFieldErrors !== apiFieldErrors || latest.t !== t) return false;

    const validation = Object.fromEntries(validationEntries);
    if (sequence === validationSequence.current) {
      setErrors((current) => equalErrors(current, validation) ? current : validation);
    }
    const invalid = Object.values(validation).some(hasErrors);
    return !invalid && !hasApiErrors;
  }, [farms, showFullValidation, initialsUsed, apiFieldErrors, hasApiErrors, t]);

  function updateFarm(index: number, value: FarmFormValueV2) {
    const previous = farms[index];
    if (!previous) return;
    const changedFields = getChangedFarmFields(previous, value);
    if (!changedFields.length) return;
    const key = farmViewKey(previous, index);

    setFarms((current) => {
      const targetIndex = current.findIndex((farm, farmIndex) => farmViewKey(farm, farmIndex) === key);
      const farm = current[targetIndex];
      if (!farm) return current;
      const updated = { ...farm };
      changedFields.forEach((field) => {
        if (field === "address.state" || field === "address.city") {
          const name = field === "address.state" ? "state" : "city";
          updated.address = { ...updated.address, [name]: value.address[name] };
        } else if (field === "season.rain_initial" || field === "season.dry_initial") {
          const name = field === "season.rain_initial" ? "rain_initial" : "dry_initial";
          updated.season = { ...updated.season, [name]: value.season[name] };
        } else {
          Object.assign(updated, { [field]: value[field as keyof FarmFormValueV2] });
        }
      });
      if (!getChangedFarmFields(farm, updated).length) return current;
      return current.map((item, farmIndex) => farmIndex === targetIndex ? updated : item);
    });
    clearApiFarmErrors(index, changedFields);
  }

  function clearApiFarmErrors(index: number, changedFields: string[]) {
    if (!changedFields.length) return;

    setApiFieldErrors((current) => {
      const next = { ...current };
      Object.keys(next).forEach((field) => {
        const match = field.match(/^farms\[(\d+)\]\.(.+)$/);
        if (!match || Number(match[1]) !== index) return;
        const formField = mapApiFarmField(field);
        if (changedFields.some((changed) => changed === formField || changed.startsWith(`${formField}.`))) delete next[field];
      });
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }

  function mapApiFarmField(field: string) {
    const fieldName = field.replace(/^farms\[\d+\]\./, "");
    const fieldMap = {
      ownerUserId: "manager_user_id",
      owner_user_id: "manager_user_id",
      state: "address.state",
      city: "address.city",
      mapCoords: "map_coords",
      map_coords: "map_coords",
    } as Record<string, string>;
    return fieldMap[fieldName] || fieldName;
  }

  function getFarmApiErrors(index: number) {
    return Object.entries(apiFieldErrors).reduce((fieldErrors, [field, message]) => {
      const match = field.match(/^farms\[(\d+)\]\.(.+)$/);
      if (!match || Number(match[1]) !== index) return fieldErrors;
      fieldErrors[mapApiFarmField(field)] = message;
      return fieldErrors;
    }, {} as ValidationErrorsV2);
  }

  function getFarmErrors(index: number) {
    return {
      ...getFarmApiErrors(index),
      ...(errors[String(index)] || {}),
    };
  }

  async function handleStateChange(index: number, state: OptionV2 | null) {
    if (!state) return;
    const data = await fetchStateCities(String(state.value));
    const farmKey = farms[index]?.uuid || String(index);
    setCitiesByFarm((current) => ({
      ...current,
      [farmKey]: data.map((city: any) => ({ label: city.nome, value: city.id })),
    }));
  }

  function addFarm() {
    const farm = createFarmBase();
    setFarms((current) => [...current, farm]);
  }

  function removeFarmCascade(farm: FarmFormValueV2) {
    const farmId = farm.uuid || farm.id;
    if (!farmId) return;

    setPayload((current) => {
      const removedAreaIds = (current?.areas || [])
        .filter((area: any) => String(area.farm_id) === String(farmId))
        .map((area: any) => String(area.uuid || area.id));
      const next = {
        ...current,
        farms: (current?.farms || []).filter((currentFarm: any) => String(currentFarm.uuid || currentFarm.id) !== String(farmId)),
        areas: (current?.areas || []).filter((area: any) => String(area.farm_id) !== String(farmId)),
        micro_areas: (current?.micro_areas || []).filter(
          (microArea: any) => !removedAreaIds.includes(String(microArea.area_id))
        ),
      };
      persistDraft(next);
      return next as any;
    });
  }

  function removeFarm(index: number) {
    if (farms.length <= 1) return;

    const key = farmViewKey(farms[index], index);
    removeFarmCascade(farms[index]);
    setFarms((current) => current.filter((farm, farmIndex) => farmViewKey(farm, farmIndex) !== key));
    setKmlErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setKmlFileNames((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  async function handleFarmKmlChange(event: ChangeEvent<HTMLInputElement>, index: number, farm: FarmFormValueV2) {
    const key = farmViewKey(farm, index);
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".kml")) {
      setKmlErrors((current) => ({ ...current, [key]: t("kmlUpload.invalidFile") }));
      event.target.value = "";
      return;
    }

    let coords: ICoords = [];

    try {
      const response = (await readFileKML(event)) as { coords: ICoords }[] | null;
      coords = response?.[0]?.coords || [];
    } catch {
      setKmlErrors((current) => ({
        ...current,
        [key]: t("kmlUpload.invalidCoordinates", { file: selectedFile.name }),
      }));
      event.target.value = "";
      return;
    }

    if (coords.length < 3) {
      setKmlErrors((current) => ({
        ...current,
        [key]: t("kmlUpload.invalidCoordinates", { file: selectedFile.name }),
      }));
      event.target.value = "";
      return;
    }

    updateFarm(index, { ...farm, map_coords: coords });
    setKmlErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setKmlFileNames((current) => ({ ...current, [key]: selectedFile.name }));
    event.target.value = "";
  }

  function navigateToFirstFarmStep(attempt = 0) {
    const progress = userProgressRef.current;
    const firstFarmSection = progress.find((section) => section.section === 2);

    if (firstFarmSection?.screens?.length) {
      setShowScreen(progress, 2, 0);
      return;
    }

    if (attempt < 10) {
      setTimeout(() => navigateToFirstFarmStep(attempt + 1), 50);
    }
  }

  async function handleNext() {
    setShowFullValidation(true);
    const isValid = await validateFarms(farms, true);
    if (!isValid) return false;

    saveDraft();
    if (importFlow) return true;
    updateStateManually(userProgress, 1, 3, "advanced");
    setTimeout(() => navigateToFirstFarmStep(), 0);
    return false;
  }

  function saveDraft() {
    const payloadFarms = farms.map(fromFarmFormValue);
    setPayload((current) => {
      const next = { ...current, farms: payloadFarms };
      persistDraft(next);
      return next as any;
    });
    return true;
  }

  useEffect(() => {
    fetchStatesUF().then((ufs) => setStates(ufs.map((uf: string) => ({ label: uf, value: uf }))));
  }, []);

  useEffect(() => {
    axios.get("/farms/user").then(({ data }) => {
      setInitialsUsed(data.map((farm: any) => farm.initials));
    });
  }, []);

  useEffect(() => {
    // Resolve saved manager IDs once their labels become available, without resetting edits.
    setFarms((current) => {
      const next = current.map((farm) => {
        const manager = managers.find((option) => String(option.value) === String(farm.manager_user_id?.value));
        return manager && manager.label !== farm.manager_user_id?.label
          ? { ...farm, manager_user_id: manager }
          : farm;
      });
      return next.some((farm, index) => farm !== current[index]) ? next : current;
    });
  }, [managers]);

  useEffect(() => {
    void validateFarms();
    return () => { validationSequence.current += 1; };
  }, [validateFarms]);

  useEffect(() => {
    const invalid = Object.values(errors).some(hasErrors);
    setBlockNextStep(invalid || hasApiErrors);
    if (!invalid && !hasApiErrors) clearStepError(screenNumber, subScreenNumber);
  }, [hasApiErrors, errors, setBlockNextStep, clearStepError, screenNumber, subScreenNumber]);

  useEffect(() => {
    userProgressRef.current = userProgress;
  }, [userProgress]);

  return (
    <>
      <main>
        <div className="onboarding-v2-form-page">
          {farms.map((farm, index) => (
            <div className="onboarding-v2-list-card" key={farm.uuid || index}>
              <FarmFormV2
                value={farm}
                managers={managers}
                states={states}
                cities={citiesByFarm[farm.uuid || String(index)] || (farm.address.city ? [farm.address.city] : [])}
                errors={getFarmErrors(index)}
                cardTitle={t("formCards.farmNumber", { number: index + 1 })}
                cardDescription={
                  farm.name
                    ? `${farm.name}${farm.initials ? ` (${farm.initials})` : ""}`
                    : farm.initials || undefined
                }
                showActions={false}
                headerActions={
                  farms.length > 1 ? (
                    <RemoveButtonV2 onClick={() => removeFarm(index)} />
                  ) : undefined
                }
                seasonSlot={
                  <div className="ov2-property-form__slider">
                    <SliderComponent
                      listOptions={Constants.MONTHS_SHORT_NAME}
                      rain_initial={farm.season.rain_initial}
                      dry_initial={farm.season.dry_initial}
                      setRainInitial={(rain_initial) => {
                        if (farm.season.rain_initial !== rain_initial) {
                          updateFarm(index, { ...farm, season: { ...farm.season, rain_initial } });
                        }
                      }}
                      setDryInitial={(dry_initial) => {
                        if (farm.season.dry_initial !== dry_initial) {
                          updateFarm(index, { ...farm, season: { ...farm.season, dry_initial } });
                        }
                      }}
                    />
                  </div>
                }
                mapSlot={
                  <div className="ov2-farm-map-slot">
                    {hasCoords(farm.map_coords) ? (
                      <>
                        <div className="ov2-farm-map-slot__toolbar">
                          <input
                            id={`farm-kml-${farmViewKey(farm, index)}`}
                            className="ov2-kml-upload__input"
                            type="file"
                            accept=".kml,application/vnd.google-earth.kml+xml"
                            onChange={(event) => handleFarmKmlChange(event, index, farm)}
                          />
                        </div>
                        <DrawOnMapSlotV2
                          value={getFarmMapCoords(farm.map_coords)}
                          initialsLabel={farm.initials || farm.name || t("formCards.farmNumber", { number: index + 1 })}
                          error={getFarmErrors(index).map_coords}
                          onChange={(map_coords) => updateFarm(index, { ...farm, map_coords })}
                        />
                      </>
                    ) : (
                      <div className="ov2-kml-upload">
                        <span className="ov2-kml-upload__eyebrow">{t("onboardingRemaining.farmMap")}</span>
                        <strong>{t("onboardingRemaining.uploadKml")}</strong>
                        <p>{t("onboardingRemaining.selectKml")}</p>
                        <label className="ov2-kml-upload__button" htmlFor={`farm-kml-${farmViewKey(farm, index)}`}>
                          {t("kmlUpload.select")}
                        </label>
                        <input
                          id={`farm-kml-${farmViewKey(farm, index)}`}
                          className="ov2-kml-upload__input"
                          type="file"
                          accept=".kml,application/vnd.google-earth.kml+xml"
                          onChange={(event) => handleFarmKmlChange(event, index, farm)}
                        />
                        {(kmlErrors[farmViewKey(farm, index)] || getFarmErrors(index).map_coords) && (
                          <small className="ov2-field__error animation__fadeIn">
                            {formatErrorMessageV2(kmlErrors[farmViewKey(farm, index)] || getFarmErrors(index).map_coords)}
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                }
                onStateChange={(state) => handleStateChange(index, state)}
                onBlur={() => validateFarms()}
                onChange={(value) => updateFarm(index, value)}
              />
            </div>
          ))}
          {!importFlow && (
            <div className="onboarding-v2-page-actions">
              <ButtonV2 onClick={addFarm}>{t("onboardingFarms.addFarm")}</ButtonV2>
            </div>
          )}
        </div>
      </main>
      <OnboardingFooterV2 nextStepEvent={handleNext} backStepEvent={importFlow ? saveDraft : undefined} />
    </>
  );
}
